<template>
  <div class="profile-page">
    <!-- 主要内容区域 -->
    <main style="margin-top: 70px;">
      <!-- 个人信息区块 -->
      <section class="profile-header">
        <div class="container">
          <div class="profile-info">
            <div class="profile-avatar">
              <div class="avatar-placeholder">
                <i class="fas fa-user"></i>
              </div>
              <button class="avatar-edit" @click="editAvatar">
                <i class="fas fa-camera"></i>
              </button>
            </div>
            <div class="profile-details">
              <h1 class="profile-name">{{ userProfile.name }}</h1>
              <p class="profile-title">{{ userProfile.title }}</p>
              <div class="profile-stats">
                <div class="stat-item">
                  <span class="stat-number">{{ userProfile.stats.courses }}</span>
                  <span class="stat-label">学习课程</span>
                </div>
                <div class="stat-item">
                  <span class="stat-number">{{ userProfile.stats.works }}</span>
                  <span class="stat-label">创作作品</span>
                </div>
                <div class="stat-item">
                  <span class="stat-number">{{ userProfile.stats.favorites }}</span>
                  <span class="stat-label">收藏内容</span>
                </div>
              </div>
            </div>
            <div class="profile-actions">
              <button class="btn btn-primary" @click="editProfile">编辑资料</button>
              <button class="btn btn-secondary" @click="openSettings">个人设置</button>
            </div>
          </div>
        </div>
      </section>

      <!-- 主要功能区域 -->
      <section class="main-dashboard">
        <div class="container">
          <div class="dashboard-layout">
            <!-- 学习中心模块 -->
            <div class="dashboard-section learning-center">
              <div class="section-header">
                <div class="section-icon">
                  <i class="fas fa-graduation-cap"></i>
                </div>
                <div class="section-title-group">
                  <h2 class="section-title">学习中心</h2>
                  <p class="section-subtitle">探索知识，传承文化</p>
                </div>
              </div>
              
              <div class="section-tabs">
                <button 
                  v-for="tab in learningTabs" 
                  :key="tab.key"
                  class="section-tab-btn"
                  :class="{ active: activeTab === tab.key }"
                  @click="switchTab(tab.key)"
                >
                  <i :class="tab.icon"></i>
                  <span>{{ tab.label }}</span>
                </button>
              </div>

              <div class="section-content">
                
                <!-- 我的学习 -->
                <div v-if="activeTab === 'learning'" class="tab-content">
                  <div class="current-courses">
                    <h3 class="content-subtitle">正在学习的课程</h3>
                    <div class="course-list">
                      <div 
                        v-for="course in currentCourses" 
                        :key="course.id"
                        class="course-item"
                      >
                        <div class="course-thumb" :class="course.thumbClass">
                          <i :class="course.icon"></i>
                        </div>
                        <div class="course-details">
                          <h4>{{ course.title }}</h4>
                          <p class="course-instructor">{{ course.instructor }}</p>
                          <div class="progress-bar">
                            <div class="progress-fill" :style="{ width: course.progress + '%' }"></div>
                          </div>
                          <span class="progress-text">进度 {{ course.progress }}% ({{ course.currentLesson }}/{{ course.totalLessons }}课时)</span>
                        </div>
                        <button class="btn btn-continue" @click="continueLearning(course)">继续学习</button>
                      </div>
                    </div>
                  </div>

                  <div class="completed-section">
                    <h3 class="content-subtitle">最近完成</h3>
                    <div class="completed-grid">
                      <div 
                        v-for="completed in completedCourses" 
                        :key="completed.id"
                        class="completed-item"
                      >
                        <i :class="completed.icon"></i>
                        <span class="completed-title">{{ completed.title }}</span>
                        <span class="completion-date">{{ completed.date }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 我的收藏 -->
                <div v-if="activeTab === 'favorites'" class="tab-content">
                  <div class="favorites-header">
                    <div class="filter-tabs">
                      <button 
                        v-for="filter in favoriteFilters" 
                        :key="filter.key"
                        class="filter-btn"
                        :class="{ active: activeFilter === filter.key }"
                        @click="switchFilter(filter.key)"
                      >
                        <i :class="filter.icon"></i>
                        <span>{{ filter.label }}</span>
                      </button>
                    </div>
                  </div>

                  <div class="favorites-grid">
                    <div 
                      v-for="favorite in filteredFavorites" 
                      :key="favorite.id"
                      class="favorite-item"
                    >
                      <div class="favorite-thumb" :class="favorite.thumbClass">
                        <i :class="favorite.icon"></i>
                      </div>
                      <div class="favorite-info">
                        <h4>{{ favorite.title }}</h4>
                        <p>{{ favorite.description }}</p>
                        <div class="favorite-meta">
                          <span class="favorite-type">{{ favorite.type }}</span>
                          <span class="favorite-date">{{ favorite.date }}</span>
                        </div>
                      </div>
                      <div class="favorite-actions">
                        <button class="btn btn-small" @click="openFavorite(favorite)">{{ favorite.actionText }}</button>
                        <button class="btn btn-outline-small" @click="removeFavorite(favorite.id)">取消收藏</button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 精选推荐 -->
                <div v-if="activeTab === 'recommend'" class="tab-content">
                  <div class="recommend-sections">
                    <div class="recommend-courses-section">
                      <h3 class="content-subtitle">为您推荐的课程</h3>
                      <div class="recommend-courses">
                        <div 
                          v-for="course in recommendedCourses" 
                          :key="course.id"
                          class="recommend-item"
                        >
                          <div class="recommend-thumb" :class="course.thumbClass">
                            <i :class="course.icon"></i>
                          </div>
                          <div class="recommend-info">
                            <h4>{{ course.title }}</h4>
                            <p>{{ course.description }}</p>
                            <div class="recommend-meta">
                              <span class="rating">{{ course.rating }} {{ course.stars }}</span>
                              <span class="students">{{ course.students }} 学员</span>
                            </div>
                          </div>
                          <button class="btn btn-recommend" @click="startLearning(course)">开始学习</button>
                        </div>
                      </div>
                    </div>

                    <div class="recommend-articles-section">
                      <h3 class="content-subtitle">热门文章推荐</h3>
                      <div class="recommend-articles">
                        <div 
                          v-for="article in recommendedArticles" 
                          :key="article.id"
                          class="article-item"
                        >
                          <div class="article-icon">
                            <i :class="article.icon"></i>
                          </div>
                          <div class="article-content">
                            <h4>{{ article.title }}</h4>
                            <p>{{ article.description }}</p>
                            <div class="article-meta">
                              <span>{{ article.views }} 阅读</span>
                              <span>{{ article.likes }} 点赞</span>
                              <span>{{ article.date }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 管理中心模块 -->
            <div class="dashboard-section management-center">
              <div class="section-header">
                <div class="section-icon">
                  <i class="fas fa-cogs"></i>
                </div>
                <div class="section-title-group">
                  <h2 class="section-title">管理中心</h2>
                  <p class="section-subtitle">个人设置与创作管理</p>
                </div>
              </div>

              <div class="section-tabs">
                <button 
                  v-for="tab in managementTabs" 
                  :key="tab.key"
                  class="section-tab-btn"
                  :class="{ active: activeManagementTab === tab.key }"
                  @click="switchManagementTab(tab.key)"
                >
                  <i :class="tab.icon"></i>
                  <span>{{ tab.label }}</span>
                </button>
              </div>

              <div class="section-content">
                
                                <!-- 创作中心 -->
                <div v-if="activeManagementTab === 'creative'" class="tab-content">
                  <div class="creative-dashboard">
                    <div class="create-actions">
                      <button 
                        v-for="action in createActions" 
                        :key="action.key"
                        class="create-btn"
                        @click="handleCreateAction(action.key)"
                      >
                        <i :class="action.icon"></i>
                        <span>{{ action.label }}</span>
                      </button>
                    </div>

                    <div class="creative-works">
                      <h3 class="content-subtitle">我的创作</h3>
                      <div class="works-list">
                        <div 
                          v-for="work in myWorks" 
                          :key="work.id"
                          class="work-item"
                        >
                          <div class="work-thumb">
                            <i :class="work.icon"></i>
                          </div>
                          <div class="work-info">
                            <h4>{{ work.title }}</h4>
                            <div class="work-stats">
                              <span><i class="fas fa-eye"></i> {{ work.views }}</span>
                              <span><i class="fas fa-heart"></i> {{ work.likes }}</span>
                              <span><i class="fas fa-comment"></i> {{ work.comments }}</span>
                            </div>
                            <span class="work-date">{{ work.date }}</span>
                          </div>
                          <div class="work-actions">
                            <button class="btn btn-small" @click="editWork(work)">编辑</button>
                            <button class="btn btn-outline-small" @click="deleteWork(work.id)">删除</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 社区动态 -->
                <div v-if="activeManagementTab === 'community'" class="tab-content">
                  <div class="community-section">
                    <div class="community-overview">
                      <div class="community-stats">
                        <div 
                          v-for="stat in communityStats" 
                          :key="stat.key"
                          class="stat-card"
                        >
                          <i :class="stat.icon"></i>
                          <div class="stat-info">
                            <span class="stat-number">{{ stat.number }}</span>
                            <span class="stat-label">{{ stat.label }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="recent-activities">
                      <h3 class="content-subtitle">最近动态</h3>
                      <div class="activity-list">
                        <div 
                          v-for="activity in recentActivities" 
                          :key="activity.id"
                          class="activity-item"
                        >
                          <div class="activity-icon">
                            <i :class="activity.icon"></i>
                          </div>
                          <div class="activity-content">
                            <p v-html="activity.content"></p>
                            <span class="activity-time">{{ activity.time }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 账号管理 -->
                <div v-if="activeManagementTab === 'account'" class="tab-content">
                  <div class="account-settings">
                    <div class="settings-grid">
                      <div class="setting-card">
                        <h3>基本信息</h3>
                        <div class="setting-items">
                          <div 
                            v-for="setting in basicSettings" 
                            :key="setting.key"
                            class="setting-item"
                          >
                            <span class="setting-label">{{ setting.label }}</span>
                            <span class="setting-value">{{ setting.value }}</span>
                            <button class="btn btn-link" @click="editSetting(setting.key)">{{ setting.action }}</button>
                          </div>
                        </div>
                      </div>

                      <div class="setting-card">
                        <h3>账号安全</h3>
                        <div class="security-items">
                          <div 
                            v-for="security in securitySettings" 
                            :key="security.key"
                            class="security-item"
                          >
                            <div class="security-info">
                              <h4>{{ security.title }}</h4>
                              <p>{{ security.description }}</p>
                            </div>
                            <button 
                              v-if="security.type === 'button'"
                              class="btn btn-small" 
                              @click="handleSecurity(security.key)"
                            >
                              {{ security.action }}
                            </button>
                            <div 
                              v-else-if="security.type === 'toggle'"
                              class="toggle-switch"
                            >
                              <input 
                                type="checkbox" 
                                :id="security.key"
                                v-model="security.enabled"
                                @change="toggleSecurity(security.key)"
                              >
                              <label :for="security.key"></label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 隐私保护 -->
                <div v-if="activeManagementTab === 'privacy'" class="tab-content">
                  <div class="privacy-settings">
                    <div class="privacy-grid">
                      <div class="privacy-card">
                        <h3>个人信息可见性</h3>
                        <div class="privacy-items">
                          <div 
                            v-for="privacy in privacySettings" 
                            :key="privacy.key"
                            class="privacy-item"
                          >
                            <div class="privacy-info">
                              <h4>{{ privacy.title }}</h4>
                              <p>{{ privacy.description }}</p>
                            </div>
                            <select 
                              v-model="privacy.value"
                              class="privacy-select"
                              @change="updatePrivacy(privacy.key)"
                            >
                              <option 
                                v-for="option in privacy.options" 
                                :key="option.value"
                                :value="option.value"
                              >
                                {{ option.label }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div class="privacy-card">
                        <h3>数据管理</h3>
                        <div class="data-actions">
                          <div 
                            v-for="action in dataActions" 
                            :key="action.key"
                            class="data-item"
                          >
                            <div class="data-info">
                              <h4>{{ action.title }}</h4>
                              <p>{{ action.description }}</p>
                            </div>
                            <button 
                              class="btn"
                              :class="action.buttonClass"
                              @click="handleDataAction(action.key)"
                            >
                              {{ action.action }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 联系我们 -->
                <div v-if="activeManagementTab === 'contact'" class="tab-content">
                  <div class="contact-section">
                    <div class="contact-grid">
                      <div class="contact-methods">
                        <div 
                          v-for="method in contactMethods" 
                          :key="method.key"
                          class="contact-item"
                        >
                          <div class="contact-icon" :class="method.iconClass">
                            <i :class="method.icon"></i>
                          </div>
                          <div class="contact-info">
                            <h4>{{ method.title }}</h4>
                            <p>{{ method.info }}</p>
                          </div>
                        </div>
                      </div>

                      <div class="feedback-form">
                        <h3>意见反馈</h3>
                        <form class="contact-form" @submit.prevent="submitFeedback">
                          <div class="form-row">
                            <select v-model="feedback.type" class="form-input" required>
                              <option value="">反馈类型</option>
                              <option value="bug">问题报告</option>
                              <option value="feature">功能建议</option>
                              <option value="content">内容问题</option>
                              <option value="other">其他</option>
                            </select>
                          </div>
                          <div class="form-row">
                            <input 
                              type="text" 
                              v-model="feedback.title"
                              class="form-input" 
                              placeholder="标题" 
                              required
                            >
                          </div>
                          <div class="form-row">
                            <textarea 
                              v-model="feedback.content"
                              class="form-textarea" 
                              rows="4" 
                              placeholder="详细描述您的问题或建议" 
                              required
                            ></textarea>
                          </div>
                          <div class="form-row">
                            <input 
                              type="text" 
                              v-model="feedback.contact"
                              class="form-input" 
                              placeholder="联系方式（选填）"
                            >
                          </div>
                          <button type="submit" class="btn btn-submit">提交反馈</button>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 社区动态 -->
                <div v-if="activeManagementTab === 'community'" class="tab-content">
                  <div class="community-section">
                    <div class="community-overview">
                      <div class="community-stats">
                        <div 
                          v-for="stat in communityStats" 
                          :key="stat.key"
                          class="stat-card"
                        >
                          <i :class="stat.icon"></i>
                          <div class="stat-info">
                            <span class="stat-number">{{ stat.number }}</span>
                            <span class="stat-label">{{ stat.label }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="recent-activities">
                      <h3 class="content-subtitle">最近动态</h3>
                      <div class="activity-list">
                        <div 
                          v-for="activity in recentActivities" 
                          :key="activity.id"
                          class="activity-item"
                        >
                          <div class="activity-icon">
                            <i :class="activity.icon"></i>
                          </div>
                          <div class="activity-content">
                            <p v-html="activity.content"></p>
                            <span class="activity-time">{{ activity.time }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 账号管理 -->
                <div v-if="activeManagementTab === 'account'" class="tab-content">
                  <div class="account-settings">
                    <div class="settings-grid">
                      <div class="setting-card">
                        <h3>基本信息</h3>
                        <div class="setting-items">
                          <div 
                            v-for="setting in basicSettings" 
                            :key="setting.key"
                            class="setting-item"
                          >
                            <span class="setting-label">{{ setting.label }}</span>
                            <span class="setting-value">{{ setting.value }}</span>
                            <button class="btn btn-link" @click="editSetting(setting.key)">{{ setting.action }}</button>
                          </div>
                        </div>
                      </div>

                      <div class="setting-card">
                        <h3>账号安全</h3>
                        <div class="security-items">
                          <div 
                            v-for="security in securitySettings" 
                            :key="security.key"
                            class="security-item"
                          >
                            <div class="security-info">
                              <h4>{{ security.title }}</h4>
                              <p>{{ security.description }}</p>
                            </div>
                            <button 
                              v-if="security.type === 'button'"
                              class="btn btn-small" 
                              @click="handleSecurity(security.key)"
                            >
                              {{ security.action }}
                            </button>
                            <div 
                              v-else-if="security.type === 'toggle'"
                              class="toggle-switch"
                            >
                              <input 
                                type="checkbox" 
                                :id="security.key"
                                v-model="security.enabled"
                                @change="toggleSecurity(security.key)"
                              >
                              <label :for="security.key"></label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 隐私保护 -->
                <div v-if="activeManagementTab === 'privacy'" class="tab-content">
                  <div class="privacy-settings">
                    <div class="privacy-grid">
                      <div class="privacy-card">
                        <h3>个人信息可见性</h3>
                        <div class="privacy-items">
                          <div 
                            v-for="privacy in privacySettings" 
                            :key="privacy.key"
                            class="privacy-item"
                          >
                            <div class="privacy-info">
                              <h4>{{ privacy.title }}</h4>
                              <p>{{ privacy.description }}</p>
                            </div>
                            <select 
                              v-model="privacy.value"
                              class="privacy-select"
                              @change="updatePrivacy(privacy.key)"
                            >
                              <option 
                                v-for="option in privacy.options" 
                                :key="option.value"
                                :value="option.value"
                              >
                                {{ option.label }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div class="privacy-card">
                        <h3>数据管理</h3>
                        <div class="data-actions">
                          <div 
                            v-for="action in dataActions" 
                            :key="action.key"
                            class="data-item"
                          >
                            <div class="data-info">
                              <h4>{{ action.title }}</h4>
                              <p>{{ action.description }}</p>
                            </div>
                            <button 
                              class="btn"
                              :class="action.buttonClass"
                              @click="handleDataAction(action.key)"
                            >
                              {{ action.action }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 联系我们 -->
                <div v-if="activeManagementTab === 'contact'" class="tab-content">
                  <div class="contact-section">
                    <div class="contact-grid">
                      <div class="contact-methods">
                        <div 
                          v-for="method in contactMethods" 
                          :key="method.key"
                          class="contact-item"
                        >
                          <div class="contact-icon" :class="method.iconClass">
                            <i :class="method.icon"></i>
                          </div>
                          <div class="contact-info">
                            <h4>{{ method.title }}</h4>
                            <p>{{ method.info }}</p>
                          </div>
                        </div>
                      </div>

                      <div class="feedback-form">
                        <h3>意见反馈</h3>
                        <form class="contact-form" @submit.prevent="submitFeedback">
                          <div class="form-row">
                            <select v-model="feedback.type" class="form-input" required>
                              <option value="">反馈类型</option>
                              <option value="bug">问题报告</option>
                              <option value="feature">功能建议</option>
                              <option value="content">内容问题</option>
                              <option value="other">其他</option>
                            </select>
                          </div>
                          <div class="form-row">
                            <input 
                              type="text" 
                              v-model="feedback.title"
                              class="form-input" 
                              placeholder="标题" 
                              required
                            >
                          </div>
                          <div class="form-row">
                            <textarea 
                              v-model="feedback.content"
                              class="form-textarea" 
                              rows="4" 
                              placeholder="详细描述您的问题或建议" 
                              required
                            ></textarea>
                          </div>
                          <div class="form-row">
                            <input 
                              type="text" 
                              v-model="feedback.contact"
                              class="form-input" 
                              placeholder="联系方式（选填）"
                            >
                          </div>
                          <button type="submit" class="btn btn-submit">提交反馈</button>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- 页面底部 -->
    <footer class="main-footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-section">
            <h4>关于我们</h4>
            <p>致力于保护和传承中华民族非物质文化遗产</p>
          </div>
          <div class="footer-section">
            <h4>快速链接</h4>
            <ul>
              <li><router-link to="/about">认识非遗</router-link></li>
              <li><router-link to="/explore">走近非遗</router-link></li>
              <li><router-link to="/heritage">传习非遗</router-link></li>
            </ul>
          </div>
          <div class="footer-section">
            <h4>联系我们</h4>
            <p>邮箱: info@heritage.cn</p>
            <p>电话: 010-12345678</p>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; 2024 中华非遗. 保留所有权利.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'ProfilePage',
  

  
  data() {
    return {
      /**
       * 用户资料信息
       */
      userProfile: {
        name: '文化爱好者',
        title: '非遗传承者 · 文化探索者',
        stats: {
          courses: 8,
          works: 23,
          favorites: 45
        }
      },
      
      /**
       * 当前激活的标签页
       */
      activeTab: 'learning',
      activeManagementTab: 'creative',
      activeFilter: 'all',
      
      /**
       * 学习中心标签页配置
       */
      learningTabs: [
        { key: 'learning', label: '我的学习', icon: 'fas fa-book-open' },
        { key: 'favorites', label: '我的收藏', icon: 'fas fa-heart' },
        { key: 'recommend', label: '精选推荐', icon: 'fas fa-star' }
      ],
      
      /**
       * 管理中心标签页配置
       */
      managementTabs: [
        { key: 'creative', label: '创作中心', icon: 'fas fa-palette' },
        { key: 'community', label: '社区动态', icon: 'fas fa-users' },
        { key: 'account', label: '账号管理', icon: 'fas fa-user-cog' },
        { key: 'privacy', label: '隐私保护', icon: 'fas fa-shield-alt' },
        { key: 'contact', label: '联系我们', icon: 'fas fa-headset' }
      ],
      
      /**
       * 正在学习的课程
       */
      currentCourses: [
        {
          id: 1,
          title: '剪纸艺术制作技艺',
          instructor: '民间艺术大师 · 国家级传承人',
          progress: 75,
          currentLesson: 9,
          totalLessons: 12,
          icon: 'fas fa-cut',
          thumbClass: 'thumb-blue'
        },
        {
          id: 2,
          title: '龙舟制作工艺',
          instructor: '造船工艺师 · 省级传承人',
          progress: 30,
          currentLesson: 6,
          totalLessons: 20,
          icon: 'fas fa-dragon',
          thumbClass: 'thumb-green'
        }
      ],
      
      /**
       * 已完成的课程
       */
      completedCourses: [
        { id: 1, title: '皮影戏艺术', icon: 'fas fa-theater-masks', date: '2024-01-15' },
        { id: 2, title: '英德红茶制作技艺', icon: 'fas fa-leaf', date: '2023-12-20' },
        { id: 3, title: '大吴泥塑工艺', icon: 'fas fa-mountain', date: '2023-11-30' }
      ],
      
      /**
       * 收藏筛选选项
       */
      favoriteFilters: [
        { key: 'all', label: '全部', icon: 'fas fa-th-large' },
        { key: 'courses', label: '课程', icon: 'fas fa-graduation-cap' },
        { key: 'articles', label: '文章', icon: 'fas fa-file-alt' },
        { key: 'videos', label: '视频', icon: 'fas fa-video' }
      ],
      
      /**
       * 收藏内容
       */
      favorites: [
        {
          id: 1,
          title: '皮影戏艺术课程',
          description: '运用光学原理的传统戏剧艺术形式',
          type: '课程',
          category: 'courses',
          date: '2024-01-10',
          icon: 'fas fa-theater-masks',
          thumbClass: 'thumb-blue',
          actionText: '开始学习'
        },
        {
          id: 2,
          title: '英德红茶制作技艺',
          description: '融合生物发酵学的传统制茶工艺',
          type: '文章',
          category: 'articles',
          date: '2024-01-05',
          icon: 'fas fa-leaf',
          thumbClass: 'thumb-green',
          actionText: '阅读文章'
        },
        {
          id: 3,
          title: '龙舟制作技艺视频',
          description: '融合流体力学的传统造船技术',
          type: '视频',
          category: 'videos',
          date: '2023-12-28',
          icon: 'fas fa-dragon',
          thumbClass: 'thumb-purple',
          actionText: '观看视频'
        },
        {
          id: 4,
          title: '大吴泥塑工艺课程',
          description: '运用地质学原理的传统泥塑艺术',
          type: '课程',
          category: 'courses',
          date: '2023-12-15',
          icon: 'fas fa-mountain',
          thumbClass: 'thumb-orange',
          actionText: '开始学习'
        },
        {
          id: 5,
          title: '剪纸艺术的几何美学',
          description: '体现材料学与几何学的民间艺术形式',
          type: '文章',
          category: 'articles',
          date: '2023-12-10',
          icon: 'fas fa-cut',
          thumbClass: 'thumb-red',
          actionText: '阅读文章'
        },
        {
          id: 6,
          title: '白沙茅龙笔制作视频',
          description: '运用生物材料科学的传统笔具制作技艺',
          type: '视频',
          category: 'videos',
          date: '2023-12-05',
          icon: 'fas fa-edit',
          thumbClass: 'thumb-teal',
          actionText: '观看视频'
        }
      ],
      
      /**
       * 推荐课程
       */
      recommendedCourses: [
        {
          id: 1,
          title: '大吴泥塑艺术',
          description: '运用地质学原理的传统泥塑艺术',
          rating: '4.8',
          stars: '★★★★★',
          students: '1.2k',
          icon: 'fas fa-mountain',
          thumbClass: 'thumb-blue'
        },
        {
          id: 2,
          title: '象牙雕刻技艺',
          description: '体现精密加工技术的传统雕刻艺术',
          rating: '4.6',
          stars: '★★★★☆',
          students: '856',
          icon: 'fas fa-cogs',
          thumbClass: 'thumb-green'
        },
        {
          id: 3,
          title: '白沙茅龙笔制作',
          description: '运用生物材料科学的传统笔具制作技艺',
          rating: '4.7',
          stars: '★★★★★',
          students: '692',
          icon: 'fas fa-edit',
          thumbClass: 'thumb-purple'
        },
        {
          id: 4,
          title: '雷州石狗雕刻',
          description: '体现地质学与雕刻艺术的传统工艺',
          rating: '4.5',
          stars: '★★★★☆',
          students: '534',
          icon: 'fas fa-dog',
          thumbClass: 'thumb-orange'
        }
      ],
      
      /**
       * 推荐文章
       */
      recommendedArticles: [
        {
          id: 1,
          title: '剪纸艺术的几何美学原理',
          description: '体现材料学与几何学的民间艺术形式',
          views: '3.2k',
          likes: '256',
          date: '2024-01-15',
          icon: 'fas fa-cut'
        },
        {
          id: 2,
          title: '白沙茅龙笔的生物材料科学',
          description: '运用生物材料科学的传统笔具制作技艺',
          views: '2.8k',
          likes: '189',
          date: '2024-01-12',
          icon: 'fas fa-edit'
        },
        {
          id: 3,
          title: '龙舟制作的流体力学原理',
          description: '融合现代科学的传统造船技术',
          views: '2.1k',
          likes: '145',
          date: '2024-01-08',
          icon: 'fas fa-dragon'
        },
        {
          id: 4,
          title: '皮影戏的光学艺术探索',
          description: '运用光学原理的传统戏剧艺术形式',
          views: '1.9k',
          likes: '132',
          date: '2024-01-05',
          icon: 'fas fa-theater-masks'
        },
        {
          id: 5,
          title: '英德红茶的发酵工艺学',
          description: '融合生物发酵学的传统制茶工艺',
          views: '1.7k',
          likes: '98',
          date: '2024-01-02',
          icon: 'fas fa-leaf'
        }
      ],
      
      /**
       * 创作操作
       */
      createActions: [
        { key: 'work', label: '发布作品', icon: 'fas fa-plus-circle' },
        { key: 'article', label: '写文章', icon: 'fas fa-edit' },
        { key: 'video', label: '上传视频', icon: 'fas fa-video' },
        { key: 'image', label: '分享图片', icon: 'fas fa-images' }
      ],
      
      /**
       * 我的创作
       */
      myWorks: [
        {
          id: 1,
          title: '我的剪纸作品集',
          views: 256,
          likes: 23,
          comments: 8,
          date: '2024-01-12',
          icon: 'fas fa-file-image'
        },
        {
          id: 2,
          title: '非遗学习心得分享',
          views: 189,
          likes: 15,
          comments: 12,
          date: '2024-01-08',
          icon: 'fas fa-file-alt'
        },
        {
          id: 3,
          title: '龙舟制作过程记录',
          views: 145,
          likes: 18,
          comments: 6,
          date: '2024-01-05',
          icon: 'fas fa-video'
        },
        {
          id: 4,
          title: '皮影戏表演技巧',
          views: 98,
          likes: 12,
          comments: 4,
          date: '2024-01-02',
          icon: 'fas fa-theater-masks'
        }
      ],
      
      /**
       * 社区统计
       */
      communityStats: [
        { key: 'posts', icon: 'fas fa-edit', number: 12, label: '发布帖子' },
        { key: 'replies', icon: 'fas fa-comment', number: 48, label: '参与回复' },
        { key: 'likes', icon: 'fas fa-heart', number: 156, label: '获得点赞' }
      ],
      
      /**
       * 最近动态
       */
      recentActivities: [
        {
          id: 1,
          icon: 'fas fa-comment-dots',
          content: '在 <strong>剪纸艺术讨论</strong> 中发表了新回复',
          time: '2小时前'
        },
        {
          id: 2,
          icon: 'fas fa-plus-circle',
          content: '发布了新帖子 <strong>非遗传承的现代意义</strong>',
          time: '1天前'
        }
      ],
      
      /**
       * 基本设置
       */
      basicSettings: [
        { key: 'username', label: '用户名', value: '文化爱好者', action: '修改' },
        { key: 'email', label: '邮箱', value: 'user@example.com', action: '验证' },
        { key: 'phone', label: '手机号', value: '138****5678', action: '更换' }
      ],
      
      /**
       * 安全设置
       */
      securitySettings: [
        {
          key: 'password',
          title: '登录密码',
          description: '定期更换密码有助于保护账号安全',
          type: 'button',
          action: '修改密码'
        },
        {
          key: 'twoFactor',
          title: '双重验证',
          description: '启用双重验证增强账号安全性',
          type: 'toggle',
          enabled: false
        }
      ],
      
      /**
       * 隐私设置
       */
      privacySettings: [
        {
          key: 'profile',
          title: '个人资料',
          description: '设置谁可以查看您的个人资料',
          value: 'friends',
          options: [
            { value: 'private', label: '仅自己' },
            { value: 'friends', label: '好友' },
            { value: 'public', label: '所有人' }
          ]
        },
        {
          key: 'learning',
          title: '学习记录',
          description: '设置学习进度和记录的可见性',
          value: 'friends',
          options: [
            { value: 'friends', label: '好友' },
            { value: 'private', label: '仅自己' },
            { value: 'public', label: '所有人' }
          ]
        }
      ],
      
      /**
       * 数据管理操作
       */
      dataActions: [
        {
          key: 'export',
          title: '数据导出',
          description: '下载您的个人数据副本',
          action: '导出数据',
          buttonClass: 'btn-outline'
        },
        {
          key: 'delete',
          title: '删除账号',
          description: '永久删除您的账号和所有相关数据',
          action: '删除账号',
          buttonClass: 'btn-danger'
        }
      ],
      
      /**
       * 联系方式
       */
      contactMethods: [
        {
          key: 'email',
          title: '邮箱联系',
          info: 'support@heritage.cn',
          icon: 'fas fa-envelope',
          iconClass: 'contact-icon-email'
        },
        {
          key: 'phone',
          title: '客服热线',
          info: '400-123-4567',
          icon: 'fas fa-phone',
          iconClass: 'contact-icon-phone'
        },
        {
          key: 'chat',
          title: '在线客服',
          info: '点击右下角AI助手',
          icon: 'fas fa-comments',
          iconClass: 'contact-icon-chat'
        }
      ],
      
      /**
       * 反馈表单数据
       */
      feedback: {
        type: '',
        title: '',
        content: '',
        contact: ''
      }
    }
  },
  
  computed: {
    /**
     * 根据筛选条件过滤收藏内容
     */
    filteredFavorites() {
      if (this.activeFilter === 'all') {
        return this.favorites;
      }
      return this.favorites.filter(item => item.category === this.activeFilter);
    }
  },
  
  methods: {
    /**
     * 切换学习中心标签页
     * @param {string} tabKey - 标签页键值
     */
    switchTab(tabKey) {
      this.activeTab = tabKey
    },
    
    /**
     * 切换管理中心标签页
     * @param {string} tabKey - 标签页键值
     */
    switchManagementTab(tabKey) {
      this.activeManagementTab = tabKey
    },
    
    /**
     * 切换收藏筛选
     * @param {string} filterKey - 筛选键值
     */
    switchFilter(filterKey) {
      this.activeFilter = filterKey
    },
    
    /**
     * 编辑头像
     */
    editAvatar() {
      this.$message.info('头像编辑功能开发中...')
    },
    
    /**
     * 编辑资料
     */
    editProfile() {
      this.$message.info('资料编辑功能开发中...')
    },
    
    /**
     * 打开设置
     */
    openSettings() {
      this.activeManagementTab = 'account'
    },
    
    /**
     * 继续学习课程
     * @param {Object} course - 课程信息
     */
    continueLearning(course) {
      this.$message.success(`继续学习：${course.title}`)
      // 可以跳转到具体的学习页面
      this.$router.push('/heritage')
    },
    
    /**
     * 打开收藏内容
     * @param {Object} favorite - 收藏项
     */
    openFavorite(favorite) {
      this.$message.success(`打开：${favorite.title}`)
    },
    
    /**
     * 移除收藏
     * @param {number} id - 收藏项ID
     */
    removeFavorite(id) {
      const index = this.favorites.findIndex(item => item.id === id)
      if (index > -1) {
        this.favorites.splice(index, 1)
        this.$message.success('已取消收藏')
      }
    },
    
    /**
     * 开始学习推荐课程
     * @param {Object} course - 课程信息
     */
    startLearning(course) {
      this.$message.success(`开始学习：${course.title}`)
      this.$router.push('/heritage')
    },
    
    /**
     * 处理创作操作
     * @param {string} actionKey - 操作键值
     */
    handleCreateAction(actionKey) {
      const actions = {
        work: '发布作品',
        article: '写文章',
        video: '上传视频',
        image: '分享图片'
      }
      this.$message.info(`${actions[actionKey]}功能开发中...`)
    },
    
    /**
     * 编辑作品
     * @param {Object} work - 作品信息
     */
    editWork(work) {
      this.$message.info(`编辑作品：${work.title}`)
    },
    
    /**
     * 删除作品
     * @param {number} id - 作品ID
     */
    deleteWork(id) {
      this.$confirm('确定要删除这个作品吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const index = this.myWorks.findIndex(work => work.id === id)
        if (index > -1) {
          this.myWorks.splice(index, 1)
          this.$message.success('删除成功')
        }
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    },
    
    /**
     * 编辑设置项
     * @param {string} key - 设置键值
     */
    editSetting(key) {
      this.$message.info(`编辑${key}功能开发中...`)
    },
    
    /**
     * 处理安全设置
     * @param {string} key - 设置键值
     */
    handleSecurity(key) {
      if (key === 'password') {
        this.$message.info('修改密码功能开发中...')
      }
    },
    
    /**
     * 切换安全设置
     * @param {string} key - 设置键值
     */
    toggleSecurity(key) {
      const setting = this.securitySettings.find(s => s.key === key)
      if (setting) {
        this.$message.success(`${setting.title}已${setting.enabled ? '启用' : '禁用'}`)
      }
    },
    
    /**
     * 更新隐私设置
     * @param {string} key - 设置键值
     */
    updatePrivacy(key) {
      const setting = this.privacySettings.find(s => s.key === key)
      if (setting) {
        this.$message.success(`${setting.title}设置已更新`)
      }
    },
    
    /**
     * 处理数据管理操作
     * @param {string} key - 操作键值
     */
    handleDataAction(key) {
      if (key === 'export') {
        this.$message.info('数据导出功能开发中...')
      } else if (key === 'delete') {
        this.$confirm('此操作将永久删除您的账号，是否继续？', '警告', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'error'
        }).then(() => {
          this.$message.info('账号删除功能开发中...')
        }).catch(() => {
          this.$message.info('已取消删除')
        })
      }
    },
    
    /**
     * 提交反馈
     */
    submitFeedback() {
      this.$message.success('感谢您的反馈，我们会尽快处理！')
      this.feedback = {
        type: '',
        title: '',
        content: '',
        contact: ''
      }
    }
  }
}
</script>

<style scoped>
/* 个人中心页面专用样式 */
.profile-page {
  min-height: 100vh;
  background: #f5f5f5;
}

/* 个人信息区块 */
.profile-header {
  background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%);
  padding: 40px 0;
  margin-bottom: 30px;
}

.profile-info {
  display: flex;
  align-items: center;
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.profile-avatar {
  position: relative;
}

.avatar-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: #e83e8c;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.avatar-edit {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e83e8c;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.avatar-edit:hover {
  background: #d91a72;
  transform: scale(1.1);
}

.profile-details {
  flex: 1;
}

.profile-name {
  font-size: 2.5rem;
  margin-bottom: 8px;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
}

.profile-title {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 20px;
}

.profile-stats {
  display: flex;
  gap: 30px;
}

.stat-item {
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  padding: 15px 20px;
  border-radius: 10px;
  backdrop-filter: blur(10px);
}

.stat-number {
  display: block;
  font-size: 2rem;
  font-weight: bold;
  color: white;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}

.profile-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.btn {
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary {
  background: white;
  color: #e83e8c;
}

.btn-primary:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* 主要功能区域 */
.main-dashboard {
  padding: 0 20px 60px;
}

.dashboard-layout {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.dashboard-section {
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
}

.section-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.learning-center .section-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.management-center .section-icon {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.section-title-group {
  flex: 1;
}

.section-title {
  font-size: 1.8rem;
  margin-bottom: 5px;
  color: #333;
}

.section-subtitle {
  color: #666;
  font-size: 0.9rem;
}

/* 标签页样式 */
.section-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 30px;
  border-bottom: 1px solid #eee;
  overflow-x: auto;
}

.section-tab-btn {
  background: none;
  border: none;
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #666;
  border-radius: 8px 8px 0 0;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.section-tab-btn:hover {
  background: #f8f9fa;
  color: #333;
}

.section-tab-btn.active {
  background: #e83e8c;
  color: white;
}

/* 内容区域样式 */
.section-content {
  min-height: 300px;
}

.tab-content {
  padding: 20px 0;
  min-height: 200px;
  display: block !important;
  opacity: 1 !important;
  visibility: visible !important;
  background: rgba(248, 249, 250, 0.5);
  border-radius: 8px;
}

.content-subtitle {
  font-size: 1.2rem;
  margin-bottom: 20px;
  color: #333;
  font-weight: 600;
}

/* 课程列表样式 */
.course-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
}

.course-item {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.course-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.course-thumb {
  width: 60px;
  height: 60px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.thumb-blue {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.thumb-green {
  background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
}

.thumb-purple {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.thumb-orange {
  background: linear-gradient(135deg, #ff9800 0%, #ff5722 100%);
}

.thumb-red {
  background: linear-gradient(135deg, #f44336 0%, #e91e63 100%);
}

.thumb-teal {
  background: linear-gradient(135deg, #009688 0%, #4caf50 100%);
}

.course-details {
  flex: 1;
}

.course-details h4 {
  font-size: 1.1rem;
  margin-bottom: 8px;
  color: #333;
}

.course-instructor {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 12px;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #e83e8c, #fd79a8);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.8rem;
  color: #666;
}

.btn-continue {
  background: #e83e8c;
  color: white;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 0.9rem;
}

.btn-continue:hover {
  background: #d91a72;
  transform: translateY(-2px);
}

/* 已完成课程网格 */
.completed-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.completed-item {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  transition: all 0.3s ease;
}

.completed-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.completed-item i {
  font-size: 2rem;
  color: #28a745;
  margin-bottom: 10px;
}

.completed-title {
  display: block;
  font-weight: 500;
  margin-bottom: 5px;
  color: #333;
}

.completion-date {
  font-size: 0.8rem;
  color: #666;
}

/* 收藏页面样式 */
.favorites-header {
  margin-bottom: 25px;
}

.filter-tabs {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  color: #666;
  transition: all 0.3s ease;
}

.filter-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.filter-btn.active {
  background: #e83e8c;
  border-color: #e83e8c;
  color: white;
}

.favorites-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.favorite-item {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.favorite-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.favorite-thumb {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
}

.favorite-info {
  flex: 1;
}

.favorite-info h4 {
  font-size: 1.1rem;
  margin-bottom: 8px;
  color: #333;
}

.favorite-info p {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 10px;
}

.favorite-meta {
  display: flex;
  gap: 15px;
  font-size: 0.8rem;
  color: #999;
}

.favorite-type {
  background: #e83e8c;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
}

.favorite-actions {
  display: flex;
  gap: 10px;
}

.btn-small {
  background: #e83e8c;
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
}

.btn-small:hover {
  background: #d91a72;
}

.btn-outline-small {
  background: transparent;
  color: #666;
  border: 1px solid #dee2e6;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
}

.btn-outline-small:hover {
  background: #f8f9fa;
  border-color: #adb5bd;
}

/* 推荐内容样式 */
.recommend-sections {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.recommend-courses,
.recommend-articles {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.recommend-item {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.recommend-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.recommend-thumb {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
}

.recommend-info {
  flex: 1;
}

.recommend-info h4 {
  font-size: 1.1rem;
  margin-bottom: 8px;
  color: #333;
}

.recommend-info p {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 10px;
}

.recommend-meta {
  display: flex;
  gap: 15px;
  font-size: 0.8rem;
  color: #999;
}

.rating {
  color: #ffc107;
}

.btn-recommend {
  background: #28a745;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
}

.btn-recommend:hover {
  background: #218838;
}

.btn-small {
  background: #e83e8c;
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
}

.btn-small:hover {
  background: #d91a72;
}

.btn-outline-small {
  background: transparent;
  color: #6c757d;
  border: 1px solid #6c757d;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
}

.btn-outline-small:hover {
  background: #6c757d;
  color: white;
}

.btn-link {
  background: none;
  color: #e83e8c;
  border: none;
  padding: 4px 8px;
  font-size: 0.8rem;
  text-decoration: underline;
}

.btn-link:hover {
  color: #d91a72;
}

/* 文章样式 */
.article-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.article-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.article-icon {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: #e83e8c;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
}

.article-content {
  flex: 1;
}

.article-content h4 {
  font-size: 1rem;
  margin-bottom: 6px;
  color: #333;
}

.article-content p {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 8px;
}

.article-meta {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #999;
}

/* 创作中心样式 */
.creative-dashboard {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.create-actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.create-btn {
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  padding: 20px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  color: #666;
}

.create-btn:hover {
  border-color: #e83e8c;
  background: rgba(232, 62, 140, 0.05);
  color: #e83e8c;
  transform: translateY(-2px);
}

.create-btn i {
  font-size: 1.5rem;
}

.works-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.work-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.work-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.work-thumb {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: #e83e8c;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
}

.work-info {
  flex: 1;
}

.work-info h4 {
  font-size: 1rem;
  margin-bottom: 8px;
  color: #333;
}

.work-stats {
  display: flex;
  gap: 15px;
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 5px;
}

.work-date {
  font-size: 0.8rem;
  color: #999;
}

.work-actions {
  display: flex;
  gap: 8px;
}

/* 社区动态样式 */
.community-section {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.community-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.stat-card {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.stat-card i {
  font-size: 1.5rem;
  color: #e83e8c;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-info .stat-number {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 2px;
}

.stat-info .stat-label {
  font-size: 0.8rem;
  color: #666;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
}

.activity-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e83e8c;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.9rem;
}

.activity-content {
  flex: 1;
}

.activity-content p {
  margin-bottom: 5px;
  color: #333;
}

.activity-time {
  font-size: 0.8rem;
  color: #999;
}

/* 设置页面样式 */
.account-settings,
.privacy-settings {
  width: 100%;
}

.settings-grid,
.privacy-grid {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.setting-card,
.privacy-card {
  background: #f8f9fa;
  padding: 25px;
  border-radius: 12px;
}

.setting-card h3,
.privacy-card h3 {
  font-size: 1.2rem;
  margin-bottom: 20px;
  color: #333;
}

.setting-items,
.privacy-items {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.setting-item,
.privacy-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 0;
  border-bottom: 1px solid #e9ecef;
}

.setting-item:last-child,
.privacy-item:last-child {
  border-bottom: none;
}

.setting-label {
  font-weight: 500;
  color: #333;
}

.setting-value {
  color: #666;
  margin-left: auto;
  margin-right: 15px;
}

.btn-link {
  background: none;
  color: #e83e8c;
  padding: 4px 8px;
  font-size: 0.9rem;
  border: none;
  cursor: pointer;
}

.btn-link:hover {
  text-decoration: underline;
}

.security-items,
.data-actions {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.security-item,
.data-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.security-info,
.privacy-info,
.data-info {
  flex: 1;
}

.security-info h4,
.privacy-info h4,
.data-info h4 {
  font-size: 1rem;
  margin-bottom: 5px;
  color: #333;
}

.security-info p,
.privacy-info p,
.data-info p {
  font-size: 0.9rem;
  color: #666;
}

/* 开关切换样式 */
.toggle-switch {
  position: relative;
  width: 50px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch label {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  border-radius: 12px;
  transition: 0.3s;
}

.toggle-switch label:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.3s;
}

.toggle-switch input:checked + label {
  background-color: #e83e8c;
}

.toggle-switch input:checked + label:before {
  transform: translateX(26px);
}

.privacy-select,
.form-input {
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.9rem;
  min-width: 120px;
}

.btn-outline {
  background: transparent;
  color: #6c757d;
  border: 1px solid #6c757d;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
}

.btn-outline:hover {
  background: #6c757d;
  color: white;
}

.btn-danger {
  background: #dc3545;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
}

.btn-danger:hover {
  background: #c82333;
}

/* 联系我们样式 */
.contact-section {
  width: 100%;
}

.contact-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.contact-methods {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
}

.contact-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
}

.contact-icon-email {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.contact-icon-phone {
  background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
}

.contact-icon-chat {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.contact-info h4 {
  font-size: 1rem;
  margin-bottom: 5px;
  color: #333;
}

.contact-info p {
  font-size: 0.9rem;
  color: #666;
}

.feedback-form {
  background: #f8f9fa;
  padding: 25px;
  border-radius: 12px;
}

.feedback-form h3 {
  font-size: 1.2rem;
  margin-bottom: 20px;
  color: #333;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-row {
  display: flex;
  flex-direction: column;
}

.form-textarea {
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.9rem;
  resize: vertical;
  font-family: inherit;
}

.btn-submit {
  background: #e83e8c;
  color: white;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 0.9rem;
  align-self: flex-start;
}

.btn-submit:hover {
  background: #d91a72;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .dashboard-layout {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .contact-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .profile-info {
    flex-direction: column;
    text-align: center;
    gap: 20px;
  }
  
  .profile-stats {
    justify-content: center;
  }
  
  .profile-actions {
    flex-direction: row;
    justify-content: center;
  }
  
  .dashboard-section {
    padding: 20px;
  }
  
  .section-tabs {
    flex-wrap: wrap;
  }
  
  .create-actions {
    grid-template-columns: 1fr;
  }
  
  .community-stats {
    grid-template-columns: 1fr;
  }
  
  .settings-grid,
  .privacy-grid {
    gap: 20px;
  }
}

@media (max-width: 480px) {
  .profile-header {
    padding: 30px 0;
  }
  
  .profile-name {
    font-size: 2rem;
  }
  
  .profile-stats {
    flex-direction: column;
    gap: 15px;
  }
  
  .main-dashboard {
    padding: 0 15px 40px;
  }
  
  .dashboard-section {
    padding: 15px;
  }
  
  .section-header {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
  
  .course-item,
  .favorite-item,
  .recommend-item {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
  
  .course-details,
  .favorite-info,
  .recommend-info {
    text-align: center;
  }
  
  .favorite-actions,
  .work-actions {
    justify-content: center;
  }
}
</style> 